import _ from 'lodash'
import type { ComputedRef, InjectionKey, Ref, WritableComputedRef } from 'vue'
import { gql, type ResultOf } from '~/tada/gql'
import { sortAwards } from '~/composables/awardWinners'
import { groupTypeMeta } from '~/composables/groupManager'

export const RESULTS_PATH = '/admin/results'

export type SectionKey = 'selection' | 'jury' | 'academy'

export type SectionMeta = {
  value: SectionKey
  /** film_groups.type holding this section's ballots. */
  groupType: string
  title: string
  icon: string
  color: string
  /** What one ballot-holder is called. */
  voter: string
  /** What results are split by. */
  category: string
  /** What a film group is called here. */
  group: string
  /** Rounds are separate lists, so places restart in each group. */
  placeByGroup?: boolean
  /**
   * The legacy Jury page averages every judge's rankings, submitted or not,
   * so unfinished ballots always count here.
   */
  countsDrafts?: boolean
  /**
   * Films with the same average place still get their own row number, in
   * team number order, as on the legacy Selection and Jury pages. Otherwise
   * (Academy, whose legacy page has no order) they share a place.
   */
  teamNumberTieBreak?: boolean
  /** Offer a switch to preview results with unfinished ballots counted. */
  previewsUnfinished?: boolean
  /** Average place where the legacy color scale runs out (10, or 6 for the Academy). */
  rankScale: number
}

export const SECTIONS: SectionMeta[] = [
  {
    value: 'selection',
    groupType: 'selection',
    voter: 'Committee member',
    category: 'Award',
    group: 'Group',
    teamNumberTieBreak: true,
    rankScale: 10,
  },
  {
    value: 'jury',
    groupType: 'judge',
    voter: 'Judge',
    category: 'Award',
    group: 'Group',
    placeByGroup: true,
    countsDrafts: true,
    teamNumberTieBreak: true,
    rankScale: 10,
  },
  {
    value: 'academy',
    groupType: 'fivefest',
    voter: 'Academy member',
    category: 'Genre',
    group: 'Flight',
    previewsUnfinished: true,
    rankScale: 6,
  },
].map(s => {
  const { title, icon, color } = groupTypeMeta(s.groupType)
  return { ...s, title, icon, color } as SectionMeta
})

export const sectionMeta = (key: string) => SECTIONS.find(s => s.value === key)

export const resultsPath = (
  year: number,
  section?: SectionKey,
  category?: string,
) => [RESULTS_PATH, year, section, category].filter(Boolean).join('/')

/** Where a film group's ballots show up in Results, if they do. */
export const groupResultsLink = (
  year: number,
  group: {
    id: number
    type: string
    award_id?: number | null
    festival_genre_id?: number | null
  },
) => {
  const section = SECTIONS.find(s => s.groupType === group.type)
  if (!section) return undefined
  const category =
    section.value === 'jury'
      ? String(group.award_id ?? `g${group.id}`)
      : section.value === 'academy'
      ? String(group.festival_genre_id ?? `g${group.id}`)
      : undefined
  return {
    path: resultsPath(year, section.value, category),
    query: { group: group.id },
  }
}

/** "Production Design/ Art Direction" → "Production Design" */
export const shortName = (name?: string | null) =>
  (name || '').split('/')[0].trim() || 'Untitled'

// Model ----------------------------------------------------------------------
// Each section's ballots are reduced to the same shape, so one set of
// components can show Selection sliders, Jury rankings and Academy rankings.

export type ResultFilm = {
  id: number
  title: string
  subtitle: string
  poster?: string
  /** Film Challenge team number, the legacy tie-break. */
  number?: number | null
}

export type ResultUser = {
  id: number
  name?: string | null
  email?: string | null
  picture?: string | null
}

/**
 * complete: counts toward results (submitted, or every score entered).
 * partial: started but not finished; the Academy can preview these.
 * empty: assigned but nothing entered yet.
 */
export type BallotState = 'complete' | 'partial' | 'empty'

export type Ballot = {
  key: string
  groupId: number
  user: ResultUser
  state: BallotState
  /** Scores entered (Selection) or films watched (Academy) out of `total`. */
  done: number
  total: number
  updatedAt?: string | null
  counted: boolean
}

export type Category = {
  /** Route segment: an award or genre id, or `g<groupId>` for an unlinked group. */
  key: string
  name: string
  shortName: string
  description?: string | null
}

export type ResultGroup = {
  id: number
  name: string
  category?: Category
  /** Set when a category is split across several groups, in creation order. */
  round?: number
  films: ResultFilm[]
  ballots: Ballot[]
}

export type Vote = {
  ballot: Ballot
  /** Place on this ballot, 1 = best. */
  rank: number
  /** Films on this ballot for this category. */
  of: number
  /** Selection only: the raw 1–100 slider value. */
  rating?: number
  /** The whole ballot this vote came from, best first, for showing the working. */
  entries: BallotEntry[]
}

export type BallotEntry = { filmId: number; rank: number; rating?: number }

export type Feedback = {
  id: number
  comments: string
  createdAt: string
  userId: number
  userName?: string | null
}

export type Standing = {
  key: string
  film: ResultFilm
  group: ResultGroup
  category: Category
  votes: Vote[]
  meanRank?: number
  meanRating?: number
  /** Place among every film in the category. */
  place?: number
  /** Place within the film's group. */
  groupPlace?: number
  feedback: Feedback[]
}

export type Results = {
  groups: ResultGroup[]
  categories: Category[]
  ballots: Ballot[]
  byCategory: Map<string, Standing[]>
}

const emptyResults = (): Results => ({
  groups: [],
  categories: [],
  ballots: [],
  byCategory: new Map(),
})

// Scoring ----------------------------------------------------------------------
// These rules reproduce the legacy Results pages exactly; the comparison
// against production depends on every detail, tie-breaks included.

/**
 * Selection ballots: a member's ratings for one award, highest first, numbered
 * 1, 2, 3… Equal ratings keep the order they're passed in, which should be
 * the films' order in the group. The selection_rankings view does the real
 * ranking (ORDER BY value DESC, sort); this only drives the explainer's
 * worked example.
 */
export const sequentialRanks = <T>(items: T[], value: (item: T) => number) =>
  new Map(_.orderBy(items, value, 'desc').map((item, i) => [item, i + 1]))

/**
 * The legacy pages color an average place on a four-step scale:
 * green, blue, orange, red, across 0–10 (0–6 for the Academy).
 */
export const rankColor = (rank: number | undefined, scale: number) => {
  if (rank == null || Number.isNaN(rank)) return 'grey'
  const colors = ['success', 'info', 'warning', 'error']
  return colors[Math.floor((rank / scale) * (colors.length - 1))] ?? 'error'
}

type BallotList = {
  ballot: Ballot
  categoryKey: string
  entries: BallotEntry[]
}

/**
 * Legacy table order: best average place first, compared as
 * (11 − average place) × 10 like the legacy columns, with equal values left
 * in team number order. Films without votes go last.
 */
const legacyOrder = (standings: Standing[]) => {
  const value = (s: Standing) =>
    s.meanRank == null ? 0 : (11 - s.meanRank) * 10
  const byNumber = _.sortBy(standings, s => s.film.number ?? Infinity)
  const voted = byNumber.filter(s => s.votes.length)
  // Array.prototype.sort is stable, as the legacy table's sort is.
  voted.sort((a, b) => value(b) - value(a))
  return [...voted, ...byNumber.filter(s => !s.votes.length)]
}

/** Academy order: average place, with equal averages sharing a place. */
const sharedOrder = (standings: Standing[]) =>
  _.sortBy(
    standings,
    s => s.meanRank ?? Infinity,
    s => s.film.title,
  )

const assignPlaces = (
  standings: Standing[],
  field: 'place' | 'groupPlace',
  shared: boolean,
) => {
  const ranked = standings.filter(s => s.votes.length)
  ranked.forEach((s, i) => {
    const prev = ranked[i - 1]
    s[field] =
      shared && prev && prev.meanRank === s.meanRank ? prev[field] : i + 1
  })
}

const tally = (
  meta: SectionMeta,
  groups: ResultGroup[],
  categories: Category[],
  eligible: (group: ResultGroup, category: Category) => ResultFilm[],
  lists: BallotList[],
  feedbackFor: (filmId: number) => Feedback[] = () => [],
) => {
  const counted = lists
    .filter(l => l.ballot.counted && l.entries.length)
    .map(l => ({ ...l, entries: _.sortBy(l.entries, 'rank') }))
  const order = meta.teamNumberTieBreak ? legacyOrder : sharedOrder
  const shared = !meta.teamNumberTieBreak
  const byCategory = new Map<string, Standing[]>()

  for (const category of categories) {
    const standings: Standing[] = []
    for (const group of groups) {
      const groupLists = counted.filter(
        l => l.ballot.groupId === group.id && l.categoryKey === category.key,
      )
      const films = eligible(group, category)
      if (!films.length) continue

      const groupStandings = films.map(film => {
        const votes: Vote[] = groupLists.flatMap(l => {
          const entry = l.entries.find(e => e.filmId === film.id)
          if (!entry) return []
          return [
            {
              ballot: l.ballot,
              rank: entry.rank,
              of: l.entries.length,
              rating: entry.rating,
              entries: l.entries,
            },
          ]
        })
        const ratings = votes.filter(v => v.rating != null)
        return {
          key: `${category.key}:${group.id}:${film.id}`,
          film,
          group,
          category,
          votes: _.sortBy(votes, 'rank', v => v.ballot.user.name),
          // Places are whole numbers, so these averages are exact.
          meanRank: votes.length ? _.meanBy(votes, 'rank') : undefined,
          meanRating: ratings.length ? _.meanBy(ratings, 'rating') : undefined,
          feedback: feedbackFor(film.id),
        } as Standing
      })
      const ordered = order(groupStandings)
      assignPlaces(ordered, 'groupPlace', shared)
      standings.push(...ordered)
    }
    const ordered = order(standings)
    assignPlaces(ordered, 'place', shared)
    byCategory.set(category.key, ordered)
  }

  return byCategory
}

const userOf = (u: {
  id: number
  name?: string | null
  email?: string | null
  picture?: string | null
}): ResultUser => ({
  id: u.id,
  name: u.name,
  email: u.email,
  picture: u.picture,
})

/** Rankings are stored as `{ [filmId]: rank }` (Jury) or `{ [filmId]: { rank } }` (Academy). */
const rankingEntries = (rankings: unknown, filmIds: Set<number>) =>
  Object.entries((rankings as Record<string, unknown>) || {}).flatMap(
    ([id, v]) => {
      const filmId = Number(id)
      const rank =
        typeof v === 'number' ? v : (v as { rank?: number } | null)?.rank
      return filmIds.has(filmId) && typeof rank === 'number'
        ? [{ filmId, rank }]
        : []
    },
  )

// Queries ----------------------------------------------------------------------

export const ResultsYearsQuery = gql(`query ResultsYears {
  selection: film_groups(distinct_on: film_festival_id, where: {type: {_eq: "selection"}, users: {id: {_is_null: false}}}) {
    id
    film_festival_id
  }
  judge: film_groups(distinct_on: film_festival_id, where: {type: {_eq: "judge"}, users: {id: {_is_null: false}}}) {
    id
    film_festival_id
  }
  fivefest: film_groups(distinct_on: film_festival_id, where: {type: {_eq: "fivefest"}, users: {id: {_is_null: false}}}) {
    id
    film_festival_id
  }
}`)

const ResultTeam = gql(`fragment ResultTeam on teams @_unmask {
  id
  team_number
  name
  film_name
  poster: assets(where: {asset_type_id: {_eq: $poster}}, order_by: {created: desc}, limit: 1) {
    id
    file_name
  }
}`)

export const SelectionResultsQuery = gql(
  `query SelectionResults($ffid: numeric!, $poster: numeric!) {
  film_groups(where: {film_festival_id: {_eq: $ffid}, type: {_eq: "selection"}}, order_by: {name: asc}) {
    id
    name
    teams(order_by: {sort: asc}) {
      id
      team {
        ...ResultTeam
        nominations: award_nominations(where: {membership: {deleted: {_is_null: true}}}) {
          id
          award_id
        }
      }
    }
    users(order_by: {id: asc}) {
      id
      user {
        id
        name
        email
        picture
      }
      ballot: selection_ballot_status {
        required
        metric_count
        is_complete
      }
    }
  }
  standings: selection_review(where: {film_festival_id: {_eq: $ffid}}, order_by: [{value: desc_nulls_last}, {team_number: asc}]) {
    film_group_id
    team_id
    award_id
    rank
    ballot_count
    rankings(order_by: {rank: asc}) {
      user_id
      value
      rank
    }
  }
  awards(where: {max_nominations: {_neq: 0}}) {
    id
    name
    description
    max_nominations
  }
}`,
  [ResultTeam],
)

export const JuryResultsQuery = gql(
  `query JuryResults($ffid: numeric!, $poster: numeric!) {
  film_groups(where: {film_festival_id: {_eq: $ffid}, type: {_eq: "judge"}}, order_by: {id: asc}) {
    id
    name
    award {
      id
      name
      description
    }
    teams(order_by: {sort: asc}) {
      id
      team {
        ...ResultTeam
      }
    }
    users(order_by: {id: asc}) {
      id
      rankings
      submitted_at
      updated_at
      user {
        id
        name
        email
        picture
      }
    }
  }
}`,
  [ResultTeam],
)

export const AcademyResultsQuery =
  gql(`query AcademyResults($ffid: numeric!, $poster: Int!) {
  film_groups(where: {film_festival_id: {_eq: $ffid}, type: {_eq: "fivefest"}}, order_by: {name: asc}) {
    id
    name
    festival_genre {
      id
      name
      description
    }
    festival_submissions(order_by: {sort: asc}) {
      id
      festival_submission {
        id
        film_title
        production_company
        genre {
          id
          name
        }
        poster: assets(where: {asset_type_id: {_eq: $poster}}, order_by: {created_at: desc}, limit: 1) {
          id
          file_name
        }
        feedback(order_by: {created_at: asc}) {
          id
          comments
          created_at
          user_id
          user {
            id
            name
          }
        }
      }
    }
    users(order_by: {id: asc}) {
      id
      rankings
      submitted_at
      updated_at
      user {
        id
        name
        email
        picture
      }
    }
  }
}`)

type Team = ResultOf<typeof ResultTeam>

const teamFilm = (team: Team): ResultFilm => ({
  id: team.id,
  title: team.film_name || team.name || `Team #${team.team_number}`,
  subtitle: `#${team.team_number} · ${team.name}`,
  poster: team.poster[0]?.file_name,
  number: team.team_number,
})

// Sections -------------------------------------------------------------------

/**
 * Committee members score every film, and each award its films were
 * nominated for, on a 1–100 slider. The math lives in the database's
 * selection_* views (backend migration
 * 1789801463120_create-selection-review-sql-views), which reproduce the
 * legacy page: only complete ballots count, each member's scores for an
 * award become places (ties follow the group's film order), places are
 * averaged, and films are listed by (11 − average) × 10, then team number.
 * Here they're only reshaped for display.
 */
const selectionResults = (
  data: ResultOf<typeof SelectionResultsQuery> | undefined,
): Results => {
  if (!data) return emptyResults()

  const awards = sortAwards(data.awards)
  const categories: Category[] = awards.map(a => ({
    key: String(a.id),
    name: a.name || 'Untitled',
    shortName: shortName(a.name),
    description: a.description,
  }))
  const openToAll = new Set(
    awards.filter(a => a.max_nominations < 0).map(a => String(a.id)),
  )

  const nominated = new Map<number, Set<string>>()
  const ballots = new Map<string, Ballot>()

  const groups: ResultGroup[] = data.film_groups.map(g => {
    const teams = g.teams.map(t => t.team)
    for (const team of teams) {
      nominated.set(
        team.id,
        new Set(team.nominations.map(n => String(n.award_id))),
      )
    }
    const groupBallots = g.users.map(({ user, ballot: status }) => {
      const done = Number(status?.metric_count ?? 0)
      const state: BallotState = status?.is_complete
        ? 'complete'
        : done
        ? 'partial'
        : 'empty'
      const ballot: Ballot = {
        key: `${g.id}:${user.id}`,
        groupId: g.id,
        user: userOf(user),
        state,
        done,
        total: Number(status?.required ?? 0),
        counted: state === 'complete',
      }
      ballots.set(ballot.key, ballot)
      return ballot
    })
    return {
      id: g.id,
      name: g.name,
      films: teams.map(teamFilm),
      ballots: groupBallots,
    }
  })
  const groupById = _.keyBy(groups, 'id')

  // View columns come back nullable, and counts and ranks as bigint.
  const rows = data.standings.map(row => ({
    groupId: Number(row.film_group_id),
    filmId: Number(row.team_id),
    categoryKey: String(row.award_id),
    meanRank: row.rank == null ? undefined : Number(row.rank),
    ballotCount: Number(row.ballot_count ?? 0),
    rankings: row.rankings.map(r => ({
      userId: Number(r.user_id),
      rank: Number(r.rank),
      rating: Number(r.value),
    })),
  }))

  // Each member's whole ballot for an award, best first, for showing the working.
  const ballotOf = (groupId: number, userId: number, categoryKey: string) =>
    `${groupId}:${userId}:${categoryKey}`
  const entries = new Map<string, BallotEntry[]>()
  for (const row of rows) {
    for (const r of row.rankings) {
      const key = ballotOf(row.groupId, r.userId, row.categoryKey)
      if (!entries.has(key)) entries.set(key, [])
      entries
        .get(key)!
        .push({ filmId: row.filmId, rank: r.rank, rating: r.rating })
    }
  }
  entries.forEach(list => list.sort((a, b) => a.rank - b.rank))

  const byCategory = new Map(categories.map(c => [c.key, [] as Standing[]]))
  // Already in the legacy order, so places are just row numbers.
  for (const row of rows) {
    const category = categories.find(c => c.key === row.categoryKey)
    const group = groupById[row.groupId]
    const film = group?.films.find(f => f.id === row.filmId)
    if (!category || !film) continue
    // The view lists every film under every award; show the ones in the running.
    if (
      !row.ballotCount &&
      !openToAll.has(category.key) &&
      !nominated.get(film.id)?.has(category.key)
    )
      continue

    const votes: Vote[] = row.rankings.flatMap(r => {
      const ballot = ballots.get(`${group.id}:${r.userId}`)
      const list = entries.get(ballotOf(group.id, r.userId, category.key))
      if (!ballot || !list) return []
      return [
        {
          ballot,
          rank: r.rank,
          of: list.length,
          rating: r.rating,
          entries: list,
        },
      ]
    })
    byCategory.get(category.key)!.push({
      key: `${category.key}:${group.id}:${film.id}`,
      film,
      group,
      category,
      votes: _.sortBy(votes, 'rank', v => v.ballot.user.name),
      meanRank: row.meanRank,
      meanRating: votes.length ? _.meanBy(votes, 'rating') : undefined,
      feedback: [],
    })
  }
  byCategory.forEach(standings => {
    assignPlaces(standings, 'place', false)
    for (const group of groups)
      assignPlaces(
        standings.filter(s => s.group.id === group.id),
        'groupPlace',
        false,
      )
  })

  return {
    groups,
    categories,
    ballots: [...ballots.values()],
    byCategory,
  }
}

/**
 * Each judge drags one award's films into order and submits. Rounds are
 * separate groups for the same award, each its own Top Ten.
 */
const juryResults = (
  data: ResultOf<typeof JuryResultsQuery> | undefined,
): Results => {
  if (!data) return emptyResults()

  const categoriesByKey = new Map<string, Category>()
  const lists: BallotList[] = []

  const groups: ResultGroup[] = data.film_groups
    // Groups with no award and nobody assigned aren't ballots (e.g. screening blocks).
    .filter(g => g.award || g.users.length)
    .map(g => {
      const category: Category = g.award
        ? {
            key: String(g.award.id),
            name: g.award.name || 'Untitled',
            shortName: shortName(g.award.name),
            description: g.award.description,
          }
        : { key: `g${g.id}`, name: g.name, shortName: g.name }
      categoriesByKey.set(category.key, category)

      const films = g.teams.map(t => teamFilm(t.team))
      const filmIds = new Set(films.map(f => f.id))
      const ballots = g.users.map(u => {
        const entries = rankingEntries(u.rankings, filmIds)
        const state: BallotState = u.submitted_at
          ? 'complete'
          : entries.length
          ? 'partial'
          : 'empty'
        const ballot: Ballot = {
          key: `${g.id}:${u.user.id}`,
          groupId: g.id,
          user: userOf(u.user),
          state,
          done: entries.length,
          total: films.length,
          updatedAt: u.submitted_at || u.updated_at,
          // Like the legacy page, every judge's ranking counts, submitted or not.
          counted: state !== 'empty',
        }
        lists.push({ ballot, categoryKey: category.key, entries })
        return ballot
      })
      return { id: g.id, name: g.name, category, films, ballots }
    })

  _.forEach(
    _.groupBy(groups, g => g.category?.key),
    rounds => {
      if (rounds.length > 1) rounds.forEach((g, i) => (g.round = i + 1))
    },
  )

  const categories = sortAwards(
    [...categoriesByKey.values()].map(c => ({
      ...c,
      id: Number(c.key) || Infinity,
    })),
  )
  const byCategory = tally(
    sectionMeta('jury')!,
    groups,
    categories,
    (group, category) =>
      group.category?.key === category.key ? group.films : [],
    lists,
  )
  return {
    groups,
    categories,
    ballots: groups.flatMap(g => g.ballots),
    byCategory,
  }
}

/**
 * Academy members join a flight of FiveFest films by watching them, rank
 * them, and submit. A genre can span several flights of different sizes.
 */
const academyResults = (
  data: ResultOf<typeof AcademyResultsQuery> | undefined,
  includePartial: boolean,
): Results => {
  if (!data) return emptyResults()

  const categoriesByKey = new Map<string, Category>()
  const feedback = new Map<number, Feedback[]>()
  const lists: BallotList[] = []

  const groups: ResultGroup[] = data.film_groups
    // Groups with no genre and nobody assigned aren't ballots (e.g. screening blocks).
    .filter(g => g.festival_genre || g.users.length)
    .map(g => {
      const genre = g.festival_genre
      const category: Category = genre
        ? {
            key: String(genre.id),
            name: genre.name,
            shortName: genre.name,
            description: genre.description,
          }
        : { key: `g${g.id}`, name: g.name, shortName: g.name }
      categoriesByKey.set(category.key, category)

      const films = g.festival_submissions.map(({ festival_submission: s }) => {
        feedback.set(
          s.id,
          s.feedback.map(f => ({
            id: f.id,
            comments: f.comments,
            createdAt: f.created_at,
            userId: f.user_id,
            userName: f.user?.name,
          })),
        )
        return {
          id: s.id,
          title: s.film_title,
          subtitle: [s.production_company, s.genre?.name]
            .filter(Boolean)
            .join(' · '),
          poster: s.poster[0]?.file_name,
        }
      })
      const filmIds = new Set(films.map(f => f.id))

      const ballots = g.users.map(u => {
        const entries = rankingEntries(u.rankings, filmIds)
        const watched = Object.values(
          (u.rankings as Record<string, { watched?: boolean }>) || {},
        ).filter(v => v && typeof v === 'object' && v.watched).length
        const state: BallotState = u.submitted_at
          ? 'complete'
          : entries.length
          ? 'partial'
          : 'empty'
        const ballot: Ballot = {
          key: `${g.id}:${u.user.id}`,
          groupId: g.id,
          user: userOf(u.user),
          state,
          done: u.submitted_at ? films.length : Math.min(watched, films.length),
          total: films.length,
          updatedAt: u.submitted_at || u.updated_at,
          counted:
            state === 'complete' || (includePartial && state === 'partial'),
        }
        lists.push({ ballot, categoryKey: category.key, entries })
        return ballot
      })
      return { id: g.id, name: g.name, category, films, ballots }
    })

  const categories = _.sortBy(
    [...categoriesByKey.values()],
    c => c.key.startsWith('g'),
    'name',
  )
  const byCategory = tally(
    sectionMeta('academy')!,
    groups,
    categories,
    (group, category) =>
      group.category?.key === category.key ? group.films : [],
    lists,
    filmId => feedback.get(filmId) || [],
  )
  return {
    groups,
    categories,
    ballots: groups.flatMap(g => g.ballots),
    byCategory,
  }
}

// Composable -------------------------------------------------------------------

export type SectionResults = {
  meta: SectionMeta
  results: ComputedRef<Results>
  loading: Ref<boolean>
  loaded: ComputedRef<boolean>
  refetch: () => void
}

/** Every section's results for one festival year. */
export const useResults = (
  festivalId: Ref<number>,
  includePartial: Ref<boolean>,
) => {
  const openFeature = useOpenFeature()
  const poster = computed(
    () => openFeature.getObject('asset_type_ids').poster as number,
  )
  const options = {
    clientId: 'admin',
    fetchPolicy: 'cache-and-network',
  } as const
  const vars = () => ({ ffid: festivalId.value, poster: poster.value })

  const selection = useQuery(SelectionResultsQuery, vars, options)
  const jury = useQuery(JuryResultsQuery, vars, options)
  const academy = useQuery(AcademyResultsQuery, vars, options)

  const section = (
    key: SectionKey,
    query: { result: Ref<any>; loading: Ref<boolean>; refetch: () => unknown },
    build: (data: any, includePartial: boolean) => Results,
  ): SectionResults => ({
    meta: sectionMeta(key)!,
    results: computed(() => build(query.result.value, includePartial.value)),
    loading: query.loading,
    loaded: computed(() => !!query.result.value),
    refetch: () => void query.refetch(),
  })

  return {
    selection: section('selection', selection, selectionResults),
    jury: section('jury', jury, juryResults),
    academy: section('academy', academy, academyResults),
  } satisfies Record<SectionKey, SectionResults>
}

export type ResultsContext = {
  year: ComputedRef<number>
  /** Group Manager only edits the current festival's groups. */
  manageable: ComputedRef<boolean>
  includePartial: WritableComputedRef<boolean>
  sections: Record<SectionKey, SectionResults>
}

export const ResultsKey: InjectionKey<ResultsContext> = Symbol('results')

export const useResultsContext = () => {
  const context = inject(ResultsKey)
  if (!context)
    throw new Error('useResultsContext must be used inside the Results page')
  return context
}

// Helpers --------------------------------------------------------------------

export const ballotCounts = (ballots: Ballot[]) => ({
  total: ballots.length,
  complete: ballots.filter(b => b.state === 'complete').length,
  partial: ballots.filter(b => b.state === 'partial').length,
  empty: ballots.filter(b => b.state === 'empty').length,
  counted: ballots.filter(b => b.counted).length,
})

/** Average places to two decimals, as the legacy pages show them. */
export const formatAvg = (rank?: number) =>
  rank == null || Number.isNaN(rank) ? '—' : rank.toFixed(2)

/**
 * Why `above` is listed ahead of `below`, in words, following the same rules
 * as the ordering itself.
 */
export const orderReason = (
  meta: SectionMeta,
  above: Standing,
  below: Standing,
) => {
  if (above.meanRank !== below.meanRank)
    return `better average place (${formatAvg(above.meanRank)} vs ${formatAvg(
      below.meanRank,
    )})`
  if (meta.teamNumberTieBreak)
    return `same average place; listed first because its team number is lower (#${above.film.number} vs #${below.film.number})`
  return 'same average place, so they share it'
}

export const formatRank = (rank?: number) =>
  rank == null ? '—' : Number.isInteger(rank) ? String(rank) : rank.toFixed(2)

export const ordinal = (n: number) => {
  const j = n % 10
  const k = n % 100
  if (j === 1 && k !== 11) return `${n}st`
  if (j === 2 && k !== 12) return `${n}nd`
  if (j === 3 && k !== 13) return `${n}rd`
  return `${n}th`
}

const csvCell = (v: unknown) => {
  const s = v == null ? '' : String(v)
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/** Download standings as a spreadsheet-friendly CSV. */
export const downloadStandings = (
  filename: string,
  meta: SectionMeta,
  standings: Standing[],
) => {
  const rows = [
    [
      'Place',
      meta.group,
      'Film',
      'Details',
      'Avg rank',
      'Avg rating',
      'Votes',
      'Ranks',
    ],
    ...standings.map(s => [
      (meta.placeByGroup ? s.groupPlace : s.place) ?? '',
      s.group.name,
      s.film.title,
      s.film.subtitle,
      s.meanRank?.toFixed(2) ?? '',
      s.meanRating?.toFixed(1) ?? '',
      s.votes.length,
      s.votes.map(v => formatRank(v.rank)).join(' '),
    ]),
  ]
  const blob = new Blob([rows.map(r => r.map(csvCell).join(',')).join('\n')], {
    type: 'text/csv',
  })
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}
